import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimationMixer, type Euler, type Object3D, type Vector3 } from 'three';
import type { BVH } from 'three/examples/jsm/loaders/BVHLoader.js';
import { useSnapshot } from 'valtio';

import {
  FINGER_BONES,
  filterAnimationClipTracksToRoot,
  quaternionsByOptionAndJoint,
} from '@cartwhl/3d/bvh';
import { inferMeshFormatFromPath } from '@cartwhl/3d/r3f/characterMeshFormat';
import Character from '@cartwhl/3d/r3f/components/Character';
import type {
  LoadFBXMesh,
  TransformLoadedMesh,
} from '@cartwhl/3d/r3f/hooks/useCharacterMeshWithCredentials';
import useFrameMixer from '@cartwhl/3d/r3f/hooks/useFrameMixer';
import { HAND_OPTIONS, type RotationOffsets } from '@cartwhl/common/types';

import {
  applyMorphTracksToBvh,
  bvhHasMorphDriverTracks,
} from '../hooks/mhrBvhMorphTracks';
import { usePlayerCharacterMeshOverrides } from '../hooks/playerCharacterMeshContext';
import { usePlayerSettingsContext } from '../hooks/playerSettingsContext';
import { useResolvedPlayerAnimation } from '../hooks/useResolvedPlayerAnimation';
import {
  type PlaceholderTransitionGhostRestoreEntry,
  applyPlaceholderTransitionGhostMaterials,
  restorePlaceholderTransitionGhostMaterials,
} from '../lib/placeholderTransitionGhostMaterials';
import {
  resolveEffectiveCharacterVisibility,
  resolveTimelineGapPlaybackVisibility,
  resolveTimelinePlaceholderTransitionGhostMode,
} from '../lib/timelineGapVisibility';
import { usePlayerStoreApi } from '../store/playerStore';
import type { PlayerAnimationSource } from '../types';

export type CharacterMeshFormat = 'fbx' | 'glb' | 'gltf';

export interface AnimatedCharacterProps {
  playerId?: string;
  actorId?: string;
  characterID: string;
  meshPath: string;
  /** Required when meshPath is a blob URL (e.g. from Cartographer). */
  meshFormat?: CharacterMeshFormat;
  animation?: PlayerAnimationSource;
  bvh?: BVH;
  bvhUrl?: string;
  rotationOffsets?: RotationOffsets;
  rotation?: Euler;
  position?: Vector3;
  staticHands?: HAND_OPTIONS | string;
  skinHex?: string;
  faceExpression?: string;
  onSceneReady?: (scene: Object3D) => void;
  onCharacterLoading?: (loading: boolean) => void;
  transformLoadedMesh?: TransformLoadedMesh;
  loadFBX?: LoadFBXMesh;
  /** Optional URL to static morph identity JSON; passed through to mesh loading (MHR rigs). */
  identityPath?: string;
  /** Head bone object name from mapping (`role_to_bone.Head`); drives name tags and overrides `head`. */
  headBoneName?: string | null;
  /** When false, the actor stays hidden regardless of timeline playback state. */
  visible?: boolean;
}

export default function AnimatedCharacter({
  playerId,
  actorId,
  characterID,
  meshPath,
  meshFormat,
  animation,
  bvh,
  bvhUrl,
  rotationOffsets,
  rotation,
  position,
  staticHands,
  skinHex,
  faceExpression,
  onSceneReady,
  onCharacterLoading,
  transformLoadedMesh,
  loadFBX,
  identityPath,
  headBoneName,
  visible = true,
}: AnimatedCharacterProps) {
  const store = usePlayerStoreApi();
  const characterMeshOverrides = usePlayerCharacterMeshOverrides();
  const resolvedMeshFormat = useMemo(
    () => meshFormat ?? inferMeshFormatFromPath(meshPath),
    [meshFormat, meshPath],
  );
  const resolvedLoadFBX = loadFBX ?? characterMeshOverrides?.loadFBX;
  const resolvedTransformLoadedMesh = useMemo(() => {
    if (transformLoadedMesh) return transformLoadedMesh;
    if (resolvedMeshFormat !== 'fbx') return undefined;
    return characterMeshOverrides?.createTransformLoadedMesh?.(meshPath);
  }, [
    characterMeshOverrides,
    meshPath,
    resolvedMeshFormat,
    transformLoadedMesh,
  ]);
  const [characterScene, setCharacterScene] = useState<Object3D | null>(null);
  const {
    settings: {
      character,
      playback: { frameRate },
    },
  } = usePlayerSettingsContext();
  const resolvedActorId = actorId ?? playerId ?? characterID;
  const { currentFrame } = useSnapshot(store.state.transport);
  const isWithinActivePlaybackClip = resolveTimelineGapPlaybackVisibility({
    animation,
    currentFrame,
    playerFrameRate: frameRate,
  });
  const isPlaceholderTransitionGhost =
    resolveTimelinePlaceholderTransitionGhostMode({
      animation,
      currentFrame,
      playerFrameRate: frameRate,
    });
  const ghostRestoreEntriesRef = useRef<
    PlaceholderTransitionGhostRestoreEntry[] | null
  >(null);
  const resolvedAnimationSource = usePlayerAnimationSource({
    animation,
    bvh,
    bvhUrl,
    rotationOffsets,
  });
  const { bvh: resolvedBvh } = useResolvedPlayerAnimation({
    actorId: resolvedActorId,
    animation: resolvedAnimationSource,
  });
  const playbackBvh = useMemo(() => {
    if (!resolvedBvh || staticHands === HAND_OPTIONS.DEFAULT || !staticHands) {
      return resolvedBvh;
    }

    const clonedBvh: BVH = {
      skeleton: resolvedBvh.skeleton.clone(),
      clip: resolvedBvh.clip.clone(),
    };

    clonedBvh.clip.tracks.forEach((track) => {
      if (!FINGER_BONES[track.name]) {
        return;
      }

      track.values = quaternionsByOptionAndJoint(
        staticHands as HAND_OPTIONS,
        track,
      );
    });

    return clonedBvh;
  }, [resolvedBvh, staticHands]);
  const bvhForMixer = useMemo(() => {
    if (!playbackBvh || !characterScene) {
      return null;
    }
    if (!bvhHasMorphDriverTracks(playbackBvh)) {
      return playbackBvh;
    }
    return applyMorphTracksToBvh(playbackBvh, characterScene);
  }, [playbackBvh, characterScene]);
  const hasAnimationSource = Boolean(resolvedAnimationSource);
  // Always mount the mesh when a path is present so a slow or failed BVH load
  // does not leave the viewer blank; animation applies once playback BVH resolves.
  const shouldRenderCharacter = Boolean(meshPath);
  const shouldShowCharacter = resolveEffectiveCharacterVisibility({
    hasAnimationSource,
    characterSceneReady: Boolean(characterScene),
    timelineGapVisible:
      isWithinActivePlaybackClip || isPlaceholderTransitionGhost,
    baseActorVisible: visible,
  });

  useEffect(() => {
    if (!characterScene) {
      return;
    }

    if (!isPlaceholderTransitionGhost) {
      if (ghostRestoreEntriesRef.current) {
        restorePlaceholderTransitionGhostMaterials(
          ghostRestoreEntriesRef.current,
        );
        ghostRestoreEntriesRef.current = null;
      }
      return;
    }

    if (ghostRestoreEntriesRef.current) {
      return;
    }

    ghostRestoreEntriesRef.current =
      applyPlaceholderTransitionGhostMaterials(characterScene);

    return () => {
      if (ghostRestoreEntriesRef.current) {
        restorePlaceholderTransitionGhostMaterials(
          ghostRestoreEntriesRef.current,
        );
        ghostRestoreEntriesRef.current = null;
      }
    };
  }, [characterScene, isPlaceholderTransitionGhost]);

  useEffect(() => {
    store.registerActor(resolvedActorId);
    return () => {
      store.unregisterActor(resolvedActorId);
    };
  }, [resolvedActorId, store]);

  useEffect(() => {
    store.setActorHeadBoneName(resolvedActorId, headBoneName);
  }, [headBoneName, resolvedActorId, store]);

  useEffect(() => {
    store.setActorMeshStatus(resolvedActorId, 'initial');
  }, [meshPath, resolvedActorId, store]);

  // Mesh often loads before the BVH; re-apply the paused frame once playback data exists.
  useEffect(() => {
    if (!characterScene || !bvhForMixer) {
      return;
    }

    const clipForPose = bvhHasMorphDriverTracks(bvhForMixer)
      ? applyMorphTracksToBvh(bvhForMixer, characterScene).clip
      : bvhForMixer.clip;
    poseCharacterScene(
      characterScene,
      clipForPose,
      Math.max(store.state.transport.currentFrame, 0) / frameRate,
    );
  }, [bvhForMixer, characterScene, frameRate, store]);

  useEffect(() => {
    if (!characterScene) {
      return;
    }

    store.registerActorScene(resolvedActorId, characterScene);
    return () => {
      store.unregisterActorScene(resolvedActorId);
    };
  }, [characterScene, resolvedActorId, store]);

  const handleSceneReady = useCallback(
    (scene: Object3D) => {
      if (playbackBvh) {
        const clipForPose = bvhHasMorphDriverTracks(playbackBvh)
          ? applyMorphTracksToBvh(playbackBvh, scene).clip
          : playbackBvh.clip;
        poseCharacterScene(
          scene,
          clipForPose,
          Math.max(store.state.transport.currentFrame, 0) / frameRate,
        );
      }

      store.setActorMeshStatus(resolvedActorId, 'ready');
      setCharacterScene((currentScene) =>
        currentScene === scene ? currentScene : scene,
      );
      onSceneReady?.(scene);
    },
    [frameRate, onSceneReady, playbackBvh, resolvedActorId, store],
  );

  const handleCharacterLoading = useCallback(
    (loading: boolean) => {
      store.setActorMeshStatus(resolvedActorId, loading ? 'pending' : 'ready');
      onCharacterLoading?.(loading);
    },
    [onCharacterLoading, resolvedActorId, store],
  );

  return (
    <>
      {shouldRenderCharacter && (
        <group visible={shouldShowCharacter}>
          <Character
            characterID={characterID}
            meshPath={meshPath}
            meshFormat={resolvedMeshFormat}
            rotation={rotation}
            position={position}
            skinHex={skinHex ?? character.skinHex}
            faceExpression={faceExpression ?? character.faceExpression}
            showSkeleton={character.showSkeleton}
            onSceneReady={handleSceneReady}
            onCharacterLoading={handleCharacterLoading}
            transformLoadedMesh={resolvedTransformLoadedMesh}
            loadFBX={resolvedLoadFBX}
            identityPath={identityPath}
            actorColorKey={resolvedActorId}
          />
        </group>
      )}
      {playbackBvh && characterScene && bvhForMixer && (
        <CharacterMixerBinding
          actorId={resolvedActorId}
          clip={bvhForMixer.clip}
          characterScene={characterScene}
        />
      )}
    </>
  );
}

function usePlayerAnimationSource({
  animation,
  bvh,
  bvhUrl,
  rotationOffsets,
}: {
  animation?: PlayerAnimationSource;
  bvh?: BVH;
  bvhUrl?: string;
  rotationOffsets?: RotationOffsets;
}) {
  const animationType = animation?.type;
  const animationBvh = animation?.type === 'bvh' ? animation.bvh : undefined;
  const animationUrl = animation?.type === 'url' ? animation.url : undefined;
  const animationUrls =
    animation?.type === 'sequence' ? animation.urls : undefined;
  const animationClipStartFrames =
    animation?.type === 'sequence' ? animation.clipStartFrames : undefined;
  const animationTotalDurationFrames =
    animation?.type === 'sequence' ? animation.totalDurationFrames : undefined;
  const animationTimelineFrameRate =
    animation?.type === 'sequence' ? animation.timelineFrameRate : undefined;
  const animationActivePlaybackRanges =
    animation?.type === 'sequence' ? animation.activePlaybackRanges : undefined;
  const animationRotationOffsets =
    animation?.type === 'url' || animation?.type === 'sequence'
      ? animation.rotationOffsets
      : undefined;

  return useMemo<PlayerAnimationSource | undefined>(() => {
    if (animationType === 'bvh' && animationBvh) {
      return { type: 'bvh', bvh: animationBvh };
    }

    if (animationType === 'url' && animationUrl !== undefined) {
      return {
        type: 'url',
        url: animationUrl,
        rotationOffsets: animationRotationOffsets ?? rotationOffsets,
      };
    }

    if (animationType === 'sequence' && animationUrls) {
      return {
        type: 'sequence',
        urls: animationUrls,
        rotationOffsets: animationRotationOffsets ?? rotationOffsets,
        clipStartFrames: animationClipStartFrames,
        totalDurationFrames: animationTotalDurationFrames,
        timelineFrameRate: animationTimelineFrameRate,
        activePlaybackRanges: animationActivePlaybackRanges,
      };
    }

    if (bvh) {
      return { type: 'bvh', bvh };
    }

    if (bvhUrl) {
      return { type: 'url', url: bvhUrl, rotationOffsets };
    }

    return undefined;
  }, [
    animationBvh,
    animationActivePlaybackRanges,
    animationClipStartFrames,
    animationRotationOffsets,
    animationTimelineFrameRate,
    animationTotalDurationFrames,
    animationType,
    animationUrl,
    animationUrls,
    bvh,
    bvhUrl,
    rotationOffsets,
  ]);
}

// Sample the animation once before the character is shown/registered so the
// first visible frame and camera bounds use an animated pose, not the bind pose.
function poseCharacterScene(scene: Object3D, clip: BVH['clip'], time: number) {
  const bindableClip = filterAnimationClipTracksToRoot(clip, scene);
  const mixer = new AnimationMixer(scene);
  const action = mixer.clipAction(bindableClip, scene);
  const poseTime = Number.isFinite(time) ? time : 0;

  action.play();
  action.time = poseTime;
  mixer.setTime(poseTime);
  scene.updateMatrixWorld(true);
}

function CharacterMixerBinding({
  actorId,
  clip,
  characterScene,
}: {
  actorId: string;
  clip: BVH['clip'];
  characterScene: Object3D;
}) {
  const store = usePlayerStoreApi();
  const {
    settings: {
      playback: { frameRate, playbackSpeed, frameStepSize, loop, disabled },
    },
  } = usePlayerSettingsContext();

  // Keep callbacks stable so the frame mixer controller does not recreate its
  // public API object and retrigger setActorMixer cleanup/setup.
  const onReady = useCallback(
    (duration: { seconds: number; frames: number }) => {
      store.setActorDuration(actorId, duration);
    },
    [actorId, store],
  );
  const mixer = useFrameMixer(clip, characterScene, {
    frameRate,
    playbackSpeed,
    frameStepSize,
    autoplay: false,
    loop,
    disabled,
    sharedTransport: true,
    onReady,
  });

  useEffect(() => {
    store.setActorMixer(actorId, mixer);
    return () => {
      store.setActorMixer(actorId, undefined);
    };
  }, [actorId, mixer, store]);

  useEffect(() => {
    store.setActorDuration(actorId, mixer.duration);
  }, [actorId, mixer.duration, store]);

  return null;
}
